/* eslint-disable react/no-array-index-key */
import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Helmet } from 'react-helmet-async';

import { loginAdmin } from '../../../services/cognito';
import { adminSessionActive } from '../../../redux/reducer/adminSession';
import { notificationError } from '../../../redux/saga/notification/actions';

import FormBuilder from '../../../helper/formBuilder';
import Schema from '../../../schema/form/auth/login';
import Button from '../../../components/button';

function BibLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const { control, handleSubmit } = useForm({
    defaultValues: Schema.DefaultValues,
    resolver: yupResolver(Schema.YupSchema),
  });

  const onSubmit = useCallback(
    async data => {
      try {
        setLoading(true);

        const response = await loginAdmin(data.email, data.password);

        dispatch(adminSessionActive(response));

        const from = location.state?.from?.pathname || '/bibs';
        navigate(from, { replace: true });
      } catch (error) {
        dispatch(notificationError(error?.message || 'Login failed. Please try again.'));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, navigate, location],
  );

  return (
    <>
      <Helmet>
        <title>Bib Distribution Login | Admin Access</title>
        <meta
          name="description"
          content="Secure login for bib distribution system administrators and authorized personnel."
        />
      </Helmet>
      <div className="min-height flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Bib Distribution Access
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Please sign in to access bib distribution system
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              {FormBuilder({
                template: Schema.Template,
                control,
              }).map(({ field }, index) => (
                <div key={index}>{field}</div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>

            <div>
              <Button
                type="submit"
                label="Sign In"
                loading={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              />
            </div>

            <div className="text-center">
              <span className="text-sm text-gray-600">
                Don&apos;t have access?{' '}
                <Link to="/contact-us" className="text-blue-600 hover:text-blue-500">
                  Contact administrator
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default BibLogin;
